'use client';

import { Hit as AlgoliaHit } from 'instantsearch.js/es/types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Button, ButtonIcon, Icon, TextButton, Typography } from '@workspace/ui/common';
import { ALGOLIA_CONFIG, AlgoliaSearchFilterTypes, cn } from '@workspace/lib';
import { Configure, Hits, Index, SearchBox, useSearchBox } from 'react-instantsearch';
import { HeaderQuerySuggestionHit, HeaderSearchHit } from './HeaderSearchHit';
import { InstantSearchProvider } from '@workspace/providers';
import { SendEventForHits } from 'instantsearch.js/es/lib/utils';
import {
  EmptyQueryBoundary,
  NoResultsBoundary,
  NoScopedResultsBoundary,
} from '../../../../widgets/AlgoliaSearch/NoResultsBoundary';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { searchBoxQueryHook } from '@/app/lib/algolia-configs/algolia-functions';
import { openDocsBot } from '@/app/lib/docsbot';
import { SearchAllHit } from '@workspace/interfaces';

interface HeaderSearchProps {
  isMobileNav?: boolean;
  onSearchToggle?: (isOpen: boolean) => void;
}

interface HeaderSearchContentProps {
  isSearchOpen: boolean;
  onSearchToggle?: (isOpen: boolean) => void;
}

const constructFilterQuery = (type: string[]) => {
  if (type.length === 0) return '';
  const filterQueries = type.map((t) => `_type:${t}`);
  return filterQueries.join(' OR ');
};

const ProductQuery = constructFilterQuery(AlgoliaSearchFilterTypes.PRODUCT);
const DocumentQuery = constructFilterQuery(AlgoliaSearchFilterTypes.DOCUMENT);
const CaseStudyQuery = constructFilterQuery(AlgoliaSearchFilterTypes.ARTICLE);

const HeaderSearchContent = ({
  isSearchOpen,
  setSearchOpen,
}: Omit<HeaderSearchContentProps, 'onSearchToggle'> & {
  setSearchOpen?: (isOpen: boolean) => void;
}) => {
  const { refine, query, clear } = useSearchBox();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();

  const EnhanceHeaderQuerySuggestionHit = useCallback(
    (props: { hit: AlgoliaHit<any>; sendEvent: SendEventForHits }) => {
      const handleClick = (hit: AlgoliaHit<any>) => {
        refine(hit.query);
      };
      return <HeaderQuerySuggestionHit {...props} onClick={handleClick} />;
    },
    [refine],
  );

  const onReset = useCallback(() => {
    clear();
    setTimeout(() => {
      setSearchOpen?.(false);
    }, 100);
  }, [clear, setSearchOpen]);

  const BoundProductHitComponentCallback = useCallback(
    ({ hit }: { hit: AlgoliaHit<SearchAllHit> }) => <HeaderSearchHit hit={hit} onClick={onReset} section="products" />,
    [onReset],
  );

  const BoundDocumentHitComponentCallback = useCallback(
    ({ hit }: { hit: AlgoliaHit<SearchAllHit> }) => <HeaderSearchHit hit={hit} onClick={onReset} section="documents" />,
    [onReset],
  );

  const BoundArticleHitComponentCallback = useCallback(
    ({ hit }: { hit: AlgoliaHit<SearchAllHit> }) => <HeaderSearchHit hit={hit} onClick={onReset} section="articles" />,
    [onReset],
  );

  const navigateToSearchPage = useCallback(
    (e?: React.MouseEvent<HTMLAnchorElement>) => {
      e?.preventDefault();
      onReset();
      router.push(`/search?query=${encodeURIComponent(query)}`, {
        scroll: false,
      });
      setTimeout(() => window.scrollTo(0, 0), 0, { behavior: 'smooth' });
    },
    [onReset, query, router],
  );

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = event.currentTarget;
      const input = form.querySelector('input[type="search"]') as HTMLInputElement;
      const query = input.value;
      if (!query.trim()) return;
      navigateToSearchPage();
    },
    [navigateToSearchPage],
  );

  useEffect(() => {
    if (!isSearchOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onReset();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchOpen, onReset]);

  return (
    <div
      ref={panelRef}
      className={cn(
        'search-panel fixed inset-0 z-9999 bg-white transition-all duration-200 h-fit overflow-y-auto scrollbar-primary',
        isSearchOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none',
      )}
    >
      <div className="wrapper px-md md:py-lg relative">
        {/* Search Input */}
        <div className="flex items-center mt-md md:mt-0 md:mb-lg pb-sm border-b border-b-primary">
          <div className="size-6 flex items-center justify-center mr-sm">
            <Icon name="Search" className="text-secondary-600" />
          </div>

          {isSearchOpen && (
            <SearchBox
              placeholder="Search here"
              classNames={{
                root: 'w-full h-6 md:h-8',
                form: 'size-full',
                input:
                  'w-full font-sora font-bold focus:outline-none bg-transparent text-secondary-900 text-lg leading-6 md:text-2xl md:leading-8 placeholder:text-lg placeholder:md:text-2xl placeholder:text-secondary-500',
                loadingIndicator: 'hidden',
                reset: 'hidden',
                submit: 'hidden',
              }}
              onSubmit={handleSubmit}
              queryHook={searchBoxQueryHook}
            />
          )}

          <Button
            onClick={onReset}
            className="p-1 text-secondary-400 hover:text-primary transition-colors duration-200 inline-flex items-center justify-center cursor-pointer"
            aria-label="Close search modal"
            overrideClasses
          >
            <Icon size={16} name="CloseBtn" />
          </Button>
        </div>

        <NoScopedResultsBoundary fallback={<div className="md:h-5 md:mb-md md:mt-lg" />}>
          <EmptyQueryBoundary fallback={<div className="md:h-5 md:mb-md md:mt-lg" />}>
            <div className="flex justify-end md:mb-md mt-sm md:mt-lg">
              <Link
                onClick={navigateToSearchPage}
                href={`/search?query=${encodeURIComponent(query)}`}
                className="text-primary font-sora text-base leading-tight font-semibold underline hover:opacity-80 transition-opacity duration-200"
              >
                View all Results
              </Link>
            </div>
          </EmptyQueryBoundary>
        </NoScopedResultsBoundary>

        {/* Content Grid */}
        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,200px)_minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,297px)_minmax(0,1fr)_minmax(0,1fr)] md:gap-10 xl:gap-16">
          {/* Popular Searches / Suggestion */}
          <div className="py-sm lg:py-0">
            <Typography variant="h6" className="mb-2 md:mb-sm">
              <EmptyQueryBoundary fallback={'Popular Searches'}>Suggestions</EmptyQueryBoundary>
            </Typography>
            <div className="space-y-xs">
              <NoResultsBoundary>
                <Hits hitComponent={EnhanceHeaderQuerySuggestionHit} />
              </NoResultsBoundary>
            </div>
          </div>

          {/* Popular Products */}
          <div className="py-sm lg:py-0">
            <Typography variant="h6" className="mb-3 lg:mb-sm">
              <EmptyQueryBoundary fallback={'Popular Products'}>Suggested Products</EmptyQueryBoundary>
            </Typography>
            <Index indexId="products-index" indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.SearchAll}>
              <Configure filters={ProductQuery} />
              <NoResultsBoundary>
                <Hits
                  hitComponent={BoundProductHitComponentCallback}
                  classNames={{
                    list: 'space-y-2',
                  }}
                />
              </NoResultsBoundary>
            </Index>
          </div>

          {/* Popular Documents */}
          <div className="py-md md:py-0">
            <Typography variant="h6" className="mb-3 md:mb-sm">
              <EmptyQueryBoundary fallback={'Popular Documents'}>Suggested Documents</EmptyQueryBoundary>
            </Typography>
            <Index indexId="documents-index" indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.SearchAll}>
              <Configure filters={DocumentQuery} />
              <NoResultsBoundary>
                <Hits
                  hitComponent={BoundDocumentHitComponentCallback}
                  classNames={{
                    list: 'flex gap-2 overflow-x-auto pb-3 md:pb-0 scrollbar-primary md:flex-col',
                  }}
                />
              </NoResultsBoundary>
            </Index>
          </div>
        </div>
      </div>

      {/* Popular Case Studies */}
      <div className="w-full md:mt-2.5 bg-accent">
        <div className="wrapper pt-md pb-3xl px-md md:py-lg">
          <Typography variant="h6" className="mb-3 md:mb-sm">
            <EmptyQueryBoundary fallback={'Popular Case Studies & Blogs'}>
              Suggested Case Studies & Blogs
            </EmptyQueryBoundary>
          </Typography>
          <Index indexId="case-studies-index" indexName={ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.SearchAll}>
            <Configure filters={CaseStudyQuery} />
            <NoResultsBoundary>
              <Hits
                hitComponent={BoundArticleHitComponentCallback}
                classNames={{
                  list: 'flex md:grid md:grid-cols-3 gap-2 overflow-x-auto pb-3 md:pb-0 scrollbar-primary',
                }}
              />
            </NoResultsBoundary>
          </Index>
        </div>
      </div>

      {/* Ask Greg */}
      <NoScopedResultsBoundary
        mode="all"
        scopedIndices={[
          ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.SearchAll,
          ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Documents,
          ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Products,
          ALGOLIA_CONFIG.LIST_INSTANT_SEARCH_INDEX.Articles,
        ]}
        // Only show the Ask Greg section when there are no results
        fallback={
          <div className="w-full md:mt-2.5 bg-white">
            <div className="wrapper p-md md:py-lg flex flex-col md:items-center gap-xs md:gap-0 items-start">
              <Typography variant="h6">Can’t find what you are looking for? Ask Greg.</Typography>
              <Typography variant="bodyMd" className="md:mb-sm md:max-w-[31.6875rem] text-black">
                Let our smart assistant help you locate the right file, recommend documents based on your project, or
                explain what’s inside a guide.
              </Typography>
              <div className="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
                <TextButton onClick={openDocsBot} className="p-0 bg-transparent font-semibold! text-primary">
                  Ask Greg
                </TextButton>
                <Icon
                  name="arrowRightIcon"
                  className="transition-transform duration-300 ease-in-out group-hover:translate-x-1 text-primary"
                />
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export const HeaderSearch = ({ onSearchToggle }: HeaderSearchProps) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Prevent body scroll when search is open
  useEffect(() => {
    if (isSearchOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isSearchOpen]);

  const handleSearchToggle = () => {
    const newState = !isSearchOpen;
    onSearchToggle?.(newState);
    setIsSearchOpen(newState);
  };

  return (
    <InstantSearchProvider indexName={ALGOLIA_CONFIG.INSTANT_SEARCH_QUERY_SUGGESTIONS}>
      <div className="relative search-container">
        <ButtonIcon
          iconName="Search"
          onClick={handleSearchToggle}
          className="p-2 text-secondary-400 hover:text-primary-500 transition-colors duration-200 active:border-none"
          aria-label="Search"
        />

        <Configure hitsPerPage={3} />
        <HeaderSearchContent isSearchOpen={isSearchOpen} setSearchOpen={setIsSearchOpen} />
      </div>
    </InstantSearchProvider>
  );
};
